import { useEffect } from 'react';
import { useRef } from 'react';
import { useState } from 'react';

export const usePolling = <Y>({
  action,
  onReceivedResult,
  delay,
  isInitiallyPaused = false,
}: {
  action: () => Promise<Y>;
  onReceivedResult: (data: Y) => void;
  delay: number;
  isInitiallyPaused?: boolean;
}) => {
  const savedAction = useRef<() => Promise<Y>>();
  const savedOnReceivedResult = useRef<(data: Y) => void>();
  const timeoutId = useRef<NodeJS.Timeout>();
  const [isPaused, setIsPaused] = useState(isInitiallyPaused);

  useEffect(() => {
    savedAction.current = action;
  }, [action]);

  useEffect(() => {
    savedOnReceivedResult.current = onReceivedResult;
  }, [onReceivedResult]);

  useEffect(() => {
    if (delay === null) return;

    const tick = async () => {
      if (!isPaused && savedAction.current) {
        const result = await savedAction.current();
        if (result && savedOnReceivedResult.current) {
          savedOnReceivedResult.current(result);
        }
        timeoutId.current = setTimeout(tick, delay);
      }
    };

    const startPolling = () => {
      timeoutId.current = setTimeout(tick, delay);
    };

    if (!isPaused) {
      startPolling();
    }

    return () => clearTimeout(timeoutId.current);
  }, [delay, isPaused]);

  return {
    pause: () => setIsPaused(true),
    resume: () => setIsPaused(false),
  };
};
